import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

// Iter403/429 — Public read-only coaching view. No login required.
// Optimised for phone-first, one card per session, players tucked
// behind a "Show players" toggle. Cards ordered by start time asc.
// Auto-refresh every 30 s. Filters out sessions whose slot was
// >30 min in the past (the backend handles that cutoff).

const BAND_DOT = {
  green: "bg-[#4CAF50]",
  unrated: "bg-[#4CAF50]",
  blue: "bg-[#66C2FF]",
  red: "bg-[#FF4500]",
  spare: "bg-[#525252]",
};

function bandDot(band) {
  const key = (band || "unrated").toLowerCase();
  return BAND_DOT[key] || BAND_DOT.unrated;
}

function fmtHHmm(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtFinish(iso, durationMinutes) {
  if (!iso || !durationMinutes) return "";
  try {
    const end = new Date(new Date(iso).getTime() + Number(durationMinutes) * 60000);
    return end.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function fmtLevel(level) {
  if (!level) return "";
  const k = String(level).toLowerCase();
  if (k === "u35") return "U3.5";
  if (k === "o35") return "O3.5";
  return level;
}

function SessionCard({ session, idx }) {
  const [open, setOpen] = useState(false);
  const bodyRef = useRef(null);
  const hasPlayers = session.players && session.players.length > 0;
  const start = fmtHHmm(session.coaching_slot_at);
  const finish = fmtFinish(session.coaching_slot_at, session.coaching_duration_minutes);
  const level = fmtLevel(session.coaching_level);
  const coach = session.coaching_coach_name || "TBC";

  return (
    <article
      className="border border-[#262626] bg-[#101010] px-4 py-4"
      data-testid={`coaching-session-${idx}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2
            className="text-lg font-bold text-white leading-tight break-words"
            data-testid={`coaching-session-name-${idx}`}
          >
            {session.coaching_session_name}
          </h2>
          <div
            className="mt-1.5 text-[11px] text-[#E0E0E0] leading-relaxed"
            data-testid={`coaching-session-meta-${idx}`}
          >
            <span data-testid={`coaching-session-court-${idx}`}>{session.court_name}</span>
            <span className="text-[#B0B0B0]"> · </span>
            <span className="text-white" data-testid={`coaching-session-time-${idx}`}>{start}</span>
            {finish && (
              <>
                <span className="text-[#B0B0B0]">–</span>
                <span className="text-white" data-testid={`coaching-session-finish-${idx}`}>{finish}</span>
              </>
            )}
            <span className="text-[#B0B0B0]"> · </span>
            <span data-testid={`coaching-session-coach-${idx}`}>Coach {coach}</span>
            {level && (
              <>
                <span className="text-[#B0B0B0]"> · </span>
                <span className="text-[#CCFF00]" data-testid={`coaching-session-level-${idx}`}>{level}</span>
              </>
            )}
          </div>
        </div>
        <div
          className="shrink-0 border border-[#CCFF00] px-2 py-1 text-[10px] uppercase tracking-widest text-[#CCFF00] self-start"
          data-testid={`coaching-session-count-${idx}`}
        >
          {session.player_count} registered
        </div>
      </header>

      {/* Iter429 — Players tucked behind a toggle when at least one
           has signed up. If no signups, we surface the "No players
           signed up yet" line directly (no toggle) — user rule. */}
      {hasPlayers ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid={`coaching-session-toggle-${idx}`}
            className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#CCFF00] hover:text-white"
          >
            <span
              aria-hidden
              className="inline-block w-2 h-2 border-t border-r border-current transition-transform duration-300"
              style={{ transform: open ? "rotate(135deg)" : "rotate(45deg)" }}
            />
            {open ? "Hide players" : "Show players"}
          </button>
          <div
            ref={bodyRef}
            className="grid transition-[grid-template-rows] duration-300 ease-out"
            style={{ gridTemplateRows: open ? "1fr" : "0fr" }}
          >
            <div className="overflow-hidden">
              <ul
                className="mt-3 divide-y divide-[#1F1F1F]"
                data-testid={`coaching-session-players-${idx}`}
              >
                {session.players.map((p, i) => (
                  <li
                    key={`${p.first_name}-${p.last_name}-${i}`}
                    className="flex items-center gap-3 py-2"
                    data-testid={`coaching-session-${idx}-player-${i}`}
                  >
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${bandDot(p.colour_band)}`}
                      aria-hidden
                      data-testid={`coaching-session-${idx}-player-${i}-dot`}
                    />
                    <span className="text-sm text-white truncate">
                      {p.first_name} {p.last_name}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      ) : (
        <div
          className="mt-3 text-xs text-[#B0B0B0] italic"
          data-testid={`coaching-session-empty-${idx}`}
        >
          No players signed up yet
        </div>
      )}
    </article>
  );
}

export default function FestivalCoachingView() {
  const { fid } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/festivals/${fid}/coaching-view`;
        const r = await axios.get(url);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load sessions");
      }
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [fid]);

  if (err) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <div className="max-w-sm w-full text-center">
          <div className="label-mono text-[#FF4500]">Error</div>
          <div className="text-sm text-[#E0E0E0] mt-2" data-testid="coaching-view-error">{err}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6">
        <div className="text-sm text-[#B0B0B0]" data-testid="coaching-view-loading">Loading sessions…</div>
      </div>
    );
  }

  const { festival, sessions } = data;
  const sorted = [...(sessions || [])].sort(
    (a, b) => String(a.coaching_slot_at || "").localeCompare(String(b.coaching_slot_at || "")),
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-16" data-testid="coaching-view-root">
      <div className="max-w-lg mx-auto px-4 pt-8 sm:px-5 sm:pt-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#CCFF00]">Rally · Lab · Coaching</div>
        <h1 className="h-display text-3xl mt-2 leading-tight" data-testid="coaching-view-fest-name">{festival.name}</h1>
        <p className="text-xs text-[#E0E0E0] mt-2 leading-relaxed">
          Today's coaching sessions and who's signed up. Ordered by start time. Auto-refreshes every 30 s.
        </p>
        <div className="h-px bg-gradient-to-r from-[#CCFF00] via-[#525252] to-transparent mt-5 mb-6" />

        {sorted.length === 0 ? (
          <div
            className="border border-[#262626] bg-[#141414] p-6 text-center"
            data-testid="coaching-view-empty"
          >
            <div className="text-sm text-[#E0E0E0]">No upcoming sessions scheduled.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="coaching-view-sessions">
            {sorted.map((s, idx) => (
              <SessionCard
                key={`${s.court_name}-${s.coaching_slot_at}-${idx}`}
                session={s}
                idx={idx}
              />
            ))}
          </div>
        )}

        <div className="mt-10 text-[10px] uppercase tracking-widest text-[#B0B0B0] text-center">
          Powered by Rally-Lab
        </div>
      </div>
    </div>
  );
}
